import type { DataSource } from '../dataSource/dataSource'
import type { Task } from '../db/models'
import { addDays, daysBetween } from '../date/calendar'
import { duplicateTaskBranch } from '../tasks/duplicateTask'
import { successorRule } from './end'
import { nextOccurrenceAfterCompletion } from './nextOccurrence'

type TaskWriter = Pick<DataSource, 'createTask' | 'copySubtasks' | 'deleteTask' | 'getTask' | 'moveSubtask' | 'moveComments' | 'moveReminders' | 'updateTask'>

/**
 * Abgehakte Aufgabe → ihr Nachfolger, nur für diese Sitzung (CONTEXT.md, Nachfolger). `moves`:
 * welche Zeile der alten Runde zu welcher des Nachfolgers wurde — der Weg der Kommentare (#182).
 */
const spawned = new Map<string, { successorId: string; moves: Map<string, string>; favoriteOrder: number | null }>()

/** Direkt hinter dem Original: die Mitte zum nächsten Nachbarn derselben Liste, sonst +1. */
function orderAfter(task: Task, siblings: readonly Task[] | undefined): number {
  const next = (siblings ?? [])
    .filter(t =>
      t.id !== task.id && !t.deletedAt
      && t.projectId === task.projectId && t.sectionId === task.sectionId
      && t.order > task.order)
    .reduce<Task | null>((min, t) => (min === null || t.order < min.order ? t : min), null)
  return next ? (task.order + next.order) / 2 : task.order + 1
}

/**
 * **Jede Wiederkehr ist eine neue Aufgabe** (ADR 0021). Legt beim Abhaken den **Nachfolger**
 * an — dieselbe Aufgabe mit allem, was sie trägt, offen, mit dem nächsten Termin und
 * unerledigten Unteraufgaben. Die erledigte bleibt, wo sie ist (ADR 0019), und geht nach
 * Geschafft. Uhrzeit/Zeitraum wandern mit dem Termin, eine Deadline im selben Abstand;
 * ein Nachfolger ohne Termin hat auch keine Uhrzeit. `siblings` (die Aufgaben der Seite)
 * bestimmen den Platz in der Liste. Geschrieben wird über `dataSource`.
 */
export async function spawnNextOccurrence(
  dataSource: TaskWriter,
  task: Task,
  completedAt: Date,
  siblings?: readonly Task[],
): Promise<Task | null> {
  if (!task.recurrence) return null
  const startDate = nextOccurrenceAfterCompletion(task.recurrence, { startDate: task.startDate, completedAt })
  // Hat die Wiederholung ein Ende erreicht, war das die letzte Runde (T13 #178).
  const recurrence = successorRule(task.recurrence, startDate, completedAt)
  if (!recurrence) return null
  const shift = task.startDate && startDate ? daysBetween(task.startDate, startDate) : null
  // Frisch gelesen: Das +1 des Abhakens steht in der Quelle, nicht im Schnappschuss der Seite.
  const stored = await dataSource.getTask(task.id)
  const keepDeadline = task.dueDate != null && shift !== null
  const { copy: successor, copied } = await duplicateTaskBranch(dataSource, task, {
    status: 'todo',
    completedAt: null,
    recurrence,
    startDate,
    startTime: startDate ? task.startTime : null,
    startPeriod: startDate ? task.startPeriod : null,
    dueDate: keepDeadline ? addDays(task.dueDate!, shift!) : null,
    dueTime: keepDeadline ? task.dueTime : null,
    duePeriod: keepDeadline ? task.duePeriod : null,
    // Eine Unteraufgabe kommt erst ans Ende und rückt unten per `moveSubtask` hinter ihre Runde —
    // ihre Nachbarn stehen in der Checkliste, nicht in `siblings`. Auf ihrem Wert zu starten,
    // hätte die ganze Checkliste neu durchnummeriert.
    order: task.parentTaskId ? undefined : orderAfter(task, siblings),
    // Der Nachfolger steht an einem ANDEREN Tag; eine dort gezogene Position
    // gibt es für ihn noch nicht. Er sortiert sich nach seiner Uhrzeit ein.
    dayOrder: null,
    // Der **Stern wandert zur nächsten Runde** (#210): In „Favoriten“ steht die aktuelle Runde,
    // nie eine Kette erledigter.
    favoriteOrder: task.favoriteOrder ?? null,
    // **Die Zähler gehören der wiederkehrenden Aufgabe, nicht der Runde** (#198, CONTEXT.md → Zähler).
    completedCount: stored?.completedCount ?? task.completedCount ?? null,
    postponedCount: stored?.postponedCount ?? task.postponedCount ?? null,
  }, { resetSubtasks: true, shiftSubtaskDays: shift ?? undefined })
  if (task.favoriteOrder != null) await dataSource.updateTask(task.id, { favoriteOrder: null })
  /*
    **Die nächste Runde einer Unteraufgabe** (#205) steht unter derselben Aufgabe, direkt hinter
    der abgehakten — die Checkliste blendet die erledigte dann aus (`withoutSucceededRounds`).
  */
  if (task.parentTaskId) await dataSource.moveSubtask(successor.id, task.id)
  /*
    **Die Kommentare wandern mit** (ADR 0021, Nachtrag): Sie gehören zur Serie, nicht zur Runde.
    Auch die ihrer Unteraufgaben — an der alten Runde stünden sie an Zeilen, die keine Liste
    mehr zeigt.
  */
  const moves = new Map([[task.id, successor.id], ...copied])
  await dataSource.moveComments(moves)
  // Die Erinnerungen ebenso: Relative gelten so jede Runde, eine einmalige feste bleibt danach still.
  await dataSource.moveReminders(moves)
  spawned.set(task.id, { successorId: successor.id, moves, favoriteOrder: task.favoriteOrder ?? null })
  return successor
}

/**
 * Der Haken zurück in derselben Sitzung nimmt den Nachfolger wieder weg. `true`, wenn es
 * einen gab. Tage später auf Geschafft wieder geöffnet bleibt er stehen — dann gibt es
 * kurz zwei offene, und der Nutzer löscht eine; zu raten, welche gemeint war, wäre schlechter.
 */
export async function retractSpawnedSuccessor(dataSource: TaskWriter, taskId: string): Promise<boolean> {
  const entry = spawned.get(taskId)
  if (!entry) return false
  spawned.delete(taskId)
  // Kommentare und Erinnerungen zurück an die alte Runde, BEVOR der Nachfolger geht — seine Kaskade nähme sie sonst mit.
  const back = new Map([...entry.moves].map(([from, to]) => [to, from]))
  await dataSource.moveComments(back)
  await dataSource.moveReminders(back)
  // Der Stern kommt mit zurück (#210) — so, wie er am Nachfolger JETZT steht: Wer ihn dort
  // entfernt oder erst gesetzt hat, meinte das auch.
  const successorStar = (await dataSource.getTask(entry.successorId))?.favoriteOrder ?? null
  await dataSource.deleteTask(entry.successorId)
  if (successorStar != null) await dataSource.updateTask(taskId, { favoriteOrder: successorStar })
  return true
}
